/**
 * Gamification Service
 * 
 * Core service for calculating travel statistics and achievements from trips
 */

import type { Trip, TripDestination } from './trips';
import type { ItineraryItem } from './itinerary';
import { fromIso2CountryCode, toIso2CountryCode } from '@/utils/countryIso2';

/**
 * Trips that count toward travel statistics: planning/upcoming trips are excluded
 * until the trip has actually ended.
 */
export function getTripsEligibleForTravelStats(trips: Trip[]): Trip[] {
  const now = Date.now();
  return trips.filter((trip) => trip.endDate.getTime() < now);
}

// Continent mapping for countries
const COUNTRY_TO_CONTINENT: Record<string, string> = {
  // North America
  'United States': 'North America',
  'USA': 'North America',
  'US': 'North America',
  'Canada': 'North America',
  'Mexico': 'North America',
  'Costa Rica': 'North America',
  'Panama': 'North America',
  'Jamaica': 'North America',
  'Cuba': 'North America',
  'Dominican Republic': 'North America',
  'Guatemala': 'North America',
  'Honduras': 'North America',
  'El Salvador': 'North America',
  'Nicaragua': 'North America',
  'Belize': 'North America',
  'Bahamas': 'North America',
  'Barbados': 'North America',
  'Trinidad and Tobago': 'North America',
  // South America
  'Brazil': 'South America',
  'Argentina': 'South America',
  'Chile': 'South America',
  'Peru': 'South America',
  'Colombia': 'South America',
  'Ecuador': 'South America',
  'Venezuela': 'South America',
  'Uruguay': 'South America',
  'Paraguay': 'South America',
  'Bolivia': 'South America',
  'Guyana': 'South America',
  'Suriname': 'South America',
  'French Guiana': 'South America',
  // Europe
  'United Kingdom': 'Europe',
  'UK': 'Europe',
  'France': 'Europe',
  'Germany': 'Europe',
  'Italy': 'Europe',
  'Spain': 'Europe',
  'Portugal': 'Europe',
  'Greece': 'Europe',
  'Netherlands': 'Europe',
  'Belgium': 'Europe',
  'Switzerland': 'Europe',
  'Austria': 'Europe',
  'Sweden': 'Europe',
  'Norway': 'Europe',
  'Denmark': 'Europe',
  'Finland': 'Europe',
  'Poland': 'Europe',
  'Czech Republic': 'Europe',
  'Hungary': 'Europe',
  'Romania': 'Europe',
  'Croatia': 'Europe',
  'Ireland': 'Europe',
  'Iceland': 'Europe',
  'Russia': 'Europe',
  'Turkey': 'Europe',
  // Asia
  'China': 'Asia',
  'Japan': 'Asia',
  'South Korea': 'Asia',
  'India': 'Asia',
  'Thailand': 'Asia',
  'Vietnam': 'Asia',
  'Singapore': 'Asia',
  'Malaysia': 'Asia',
  'Indonesia': 'Asia',
  'Philippines': 'Asia',
  'Taiwan': 'Asia',
  'Hong Kong': 'Asia',
  'United Arab Emirates': 'Asia',
  'UAE': 'Asia',
  'Saudi Arabia': 'Asia',
  'Israel': 'Asia',
  'Jordan': 'Asia',
  'Lebanon': 'Asia',
  'Nepal': 'Asia',
  'Sri Lanka': 'Asia',
  'Bangladesh': 'Asia',
  'Pakistan': 'Asia',
  // Africa
  'South Africa': 'Africa',
  'Egypt': 'Africa',
  'Morocco': 'Africa',
  'Kenya': 'Africa',
  'Tanzania': 'Africa',
  'Ghana': 'Africa',
  'Nigeria': 'Africa',
  'Ethiopia': 'Africa',
  'Tunisia': 'Africa',
  'Algeria': 'Africa',
  // Oceania
  'Australia': 'Oceania',
  'New Zealand': 'Oceania',
  'Fiji': 'Oceania',
  'Papua New Guinea': 'Oceania',
  'Samoa': 'Oceania',
  'Tonga': 'Oceania',
};

/**
 * Get continent for a country name
 */
export function getContinentForCountry(country: string): string | null {
  if (!country) return null;
  
  // Try exact match first
  const continent = COUNTRY_TO_CONTINENT[country];
  if (continent) return continent;
  
  // Try case-insensitive match
  const countryLower = country.toLowerCase();
  for (const [key, value] of Object.entries(COUNTRY_TO_CONTINENT)) {
    if (key.toLowerCase() === countryLower) {
      return value;
    }
  }
  
  return null;
}

function normalizeCountryName(country?: string | null): string {
  const trimmed = country?.trim() ?? '';
  if (!trimmed) return '';

  const iso2 = toIso2CountryCode(trimmed);
  if (!iso2) {
    return trimmed;
  }
  return fromIso2CountryCode(iso2) ?? trimmed;
}

/**
 * Extract unique countries from trips
 */
export function getCountriesFromTrips(trips: Trip[]): Array<{
  country: string;
  firstVisit: Date;
  visitCount: number;
  years: number[];
}> {
  const countryMap = new Map<string, {
    country: string;
    firstVisit: Date;
    visitCount: number;
    years: Set<number>;
  }>();

  for (const trip of trips) {
    const tripYear = trip.startDate.getFullYear();
    
    // Handle multi-destination trips
    if (trip.destinations && trip.destinations.length > 0) {
      for (const dest of trip.destinations) {
        if (dest.country) {
          const country = normalizeCountryName(dest.country);
          if (!country) continue;
          const countryKey = country.toLowerCase();
          
          const existing = countryMap.get(countryKey);
          if (existing) {
            existing.visitCount += 1;
            existing.years.add(tripYear);
            if (dest.startDate < existing.firstVisit) {
              existing.firstVisit = dest.startDate;
            }
          } else {
            countryMap.set(countryKey, {
              country,
              firstVisit: dest.startDate,
              visitCount: 1,
              years: new Set([tripYear]),
            });
          }
        }
      }
    }
    
    // Handle legacy single destination field
    if (trip.destination && !trip.destinations) {
      // Try to extract country from destination string (e.g., "Paris, France")
      const parts = trip.destination.split(',').map(p => p.trim());
      if (parts.length > 1) {
        const country = normalizeCountryName(parts[parts.length - 1]);
        if (country) {
          const countryKey = country.toLowerCase();
          const existing = countryMap.get(countryKey);
          if (existing) {
            existing.visitCount += 1;
            existing.years.add(tripYear);
            if (trip.startDate < existing.firstVisit) {
              existing.firstVisit = trip.startDate;
            }
          } else {
            countryMap.set(countryKey, {
              country,
              firstVisit: trip.startDate,
              visitCount: 1,
              years: new Set([tripYear]),
            });
          }
        }
      }
    }
  }

  return Array.from(countryMap.values()).map((data) => ({
    country: data.country,
    firstVisit: data.firstVisit,
    visitCount: data.visitCount,
    years: Array.from(data.years).sort((a, b) => a - b),
  }));
}

/**
 * Extract unique cities from trips
 */
export function getCitiesFromTrips(trips: Trip[]): Array<{
  city: string;
  country: string;
  firstVisit: Date;
  visitCount: number;
}> {
  const cityMap = new Map<string, {
    country: string;
    firstVisit: Date;
    visitCount: number;
  }>();

  for (const trip of trips) {
    // Handle multi-destination trips
    if (trip.destinations && trip.destinations.length > 0) {
      for (const dest of trip.destinations) {
        const city = dest.city.trim();
        if (!city) continue;

        const country = normalizeCountryName(dest.country || '');
        const cityKey = `${city.toLowerCase()}_${country.toLowerCase()}`;
        const existing = cityMap.get(cityKey);
        
        if (existing) {
          existing.visitCount += 1;
          if (dest.startDate < existing.firstVisit) {
            existing.firstVisit = dest.startDate;
          }
        } else {
          cityMap.set(cityKey, {
            country,
            firstVisit: dest.startDate,
            visitCount: 1,
          });
        }
      }
    }
    
    // Handle legacy single destination field
    if (trip.destination && !trip.destinations) {
      const parts = trip.destination.split(',').map(p => p.trim());
      const city = parts[0];
      const country = normalizeCountryName(parts.length > 1 ? parts[parts.length - 1] : '');
      
      if (city) {
        const cityKey = `${city.toLowerCase()}_${country.toLowerCase()}`;
        const existing = cityMap.get(cityKey);
        
        if (existing) {
          existing.visitCount += 1;
          if (trip.startDate < existing.firstVisit) {
            existing.firstVisit = trip.startDate;
          }
        } else {
          cityMap.set(cityKey, {
            country,
            firstVisit: trip.startDate,
            visitCount: 1,
          });
        }
      }
    }
  }

  return Array.from(cityMap.entries()).map(([cityKey, data]) => {
    const [city] = cityKey.split('_');
    return {
      city: city.charAt(0).toUpperCase() + city.slice(1), // Capitalize first letter
      country: data.country,
      firstVisit: data.firstVisit,
      visitCount: data.visitCount,
    };
  });
}

/**
 * Calculate total trip days from all trips
 */
export function calculateTotalTripDays(trips: Trip[]): number {
  let totalDays = 0;
  
  for (const trip of trips) {
    const startDate = trip.startDate;
    const endDate = trip.endDate;
    const days = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    totalDays += days;
  }
  
  return totalDays;
}

/**
 * Calculate continents visited from countries
 */
export function getContinentsFromCountries(
  countries: Array<{ country: string }>
): string[] {
  const continents = new Set<string>();
  
  for (const { country } of countries) {
    const continent = getContinentForCountry(country);
    if (continent) {
      continents.add(continent);
    }
  }
  
  return Array.from(continents).sort();
}

/**
 * Calculate travel streak (consecutive months with trips)
 */
export function calculateTravelStreak(trips: Trip[]): {
  currentStreak: number;
  longestStreak: number;
} {
  if (trips.length === 0) {
    return { currentStreak: 0, longestStreak: 0 };
  }

  // Get all months with trips
  const monthsWithTrips = new Set<string>();
  for (const trip of trips) {
    const year = trip.startDate.getFullYear();
    const month = trip.startDate.getMonth();
    monthsWithTrips.add(`${year}-${month.toString().padStart(2, '0')}`);
    
    // Also add end date month if different
    const endYear = trip.endDate.getFullYear();
    const endMonth = trip.endDate.getMonth();
    if (endYear !== year || endMonth !== month) {
      monthsWithTrips.add(`${endYear}-${endMonth.toString().padStart(2, '0')}`);
    }
  }

  const sortedMonths = Array.from(monthsWithTrips).sort();
  
  if (sortedMonths.length === 0) {
    return { currentStreak: 0, longestStreak: 0 };
  }

  // Calculate longest streak
  let longestStreak = 1;
  let currentLongest = 1;
  
  for (let i = 1; i < sortedMonths.length; i++) {
    const prev = sortedMonths[i - 1];
    const curr = sortedMonths[i];
    
    const [prevYear, prevMonth] = prev.split('-').map(Number);
    const [currYear, currMonth] = curr.split('-').map(Number);
    
    // Check if months are consecutive
    let isConsecutive = false;
    if (currYear === prevYear && currMonth === prevMonth + 1) {
      isConsecutive = true;
    } else if (currYear === prevYear + 1 && prevMonth === 11 && currMonth === 0) {
      isConsecutive = true;
    }
    
    if (isConsecutive) {
      currentLongest += 1;
      longestStreak = Math.max(longestStreak, currentLongest);
    } else {
      currentLongest = 1;
    }
  }

  // Calculate current streak (from most recent month)
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const currentMonthKey = `${currentYear}-${currentMonth.toString().padStart(2, '0')}`;
  
  let currentStreak = 0;
  let checkMonth = currentMonthKey;
  let checkDate = new Date(currentYear, currentMonth);
  
  while (sortedMonths.includes(checkMonth)) {
    currentStreak += 1;
    checkDate.setMonth(checkDate.getMonth() - 1);
    const year = checkDate.getFullYear();
    const month = checkDate.getMonth();
    checkMonth = `${year}-${month.toString().padStart(2, '0')}`;
  }

  return { currentStreak, longestStreak };
}

/**
 * Get statistics for a specific year
 */
export function getYearlyStats(trips: Trip[], year: number): {
  countries: number;
  cities: number;
  tripDays: number;
  trips: number;
} {
  const completedTrips = getTripsEligibleForTravelStats(trips);
  const yearTrips = completedTrips.filter(
    trip => trip.startDate.getFullYear() === year || trip.endDate.getFullYear() === year
  );
  
  const countries = getCountriesFromTrips(yearTrips);
  const cities = getCitiesFromTrips(yearTrips);
  const tripDays = calculateTotalTripDays(yearTrips);
  
  return {
    countries: countries.length,
    cities: cities.length,
    tripDays,
    trips: yearTrips.length,
  };
}

/**
 * Extract landmark visits from itinerary items
 */
export function extractLandmarkVisits(
  itineraryItems: ItineraryItem[]
): Array<{
  landmarkId: string;
  name: string;
  location: string;
  visitDate: Date;
}> {
  const visits: Array<{
    landmarkId: string;
    name: string;
    location: string;
    visitDate: Date;
  }> = [];

  for (const item of itineraryItems) {
    // Only count completed activity items as landmark visits
    if (item.category === 'activity' && item.status === 'completed' && item.startDate) {
      visits.push({
        landmarkId: item.id,
        name: item.title,
        location: item.location || item.address || '',
        visitDate: item.startDate,
      });
    }
  }

  return visits;
}

